import {useAtomValue} from "jotai";
import {reportsInventoryDetail, reportsStaticsInventoryDetailColumn,} from "../../pages/reports/entity/inventory";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import styled from "styled-components";
import {ResponsiveBar} from "@nivo/bar";
import {sort} from "./sortList";
import {
  selectAdminStaticsInventoryDetail,
  selectUserStaticsInventoryDetail
} from "../../services/reports/inventoryAxios";
import {selectStaticsMediaDetail} from "../../services/reports/mediaAxios"
import {ModalBody, ModalHeader} from "../modal/Modal";
import {ReportsCondition} from "./Condition";
import Table from "../table";
import HorizontalScrollGuide from "../table/HorizontalScrollGuide";
import {
  reportsMediaDetailAtom,
  reportsStaticsMediaDetailColumn
} from "../../pages/reports/entity/media";
import {lockedRows, summaryReducer} from "../../pages/reports/entity/common";
import {tokenResultAtom} from "../../pages/login/entity";
import {decimalFormat} from "../../common/StringUtils";
import {calculateEcpm, formatEcpm, metricColor} from "../../pages/reports/entity/metrics";

const formatRevenue = value => Number(value || 0).toLocaleString("ko-KR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function InventoryDailyChart({data, metric}) {
  const formatChartDate = value => {
    const digits = String(value || "").replace(/\D/g, "");
    return digits.length >= 8 ? `${digits.slice(4,6)}.${digits.slice(6,8)}` : String(value || "");
  };
  const visibleDates = data.length > 31
      ? data.filter((_,index) => index % 5 === 0).map(item => item.historyDate)
      : data.map(item => item.historyDate);
  const formatValue = value => metric === "revenueAmount"
      ? formatRevenue(value)
      : metric === "ecpm" ? formatEcpm(value) : decimalFormat(value);
  return <ResponsiveBar
      data={data}
      keys={[metric]}
      indexBy="historyDate"
      height={280}
      margin={{top: 20, right: 24, bottom: 62, left: 62}}
      padding={0.45}
      valueScale={{type:"linear"}}
      indexScale={{type:"band",round:true}}
      colors={[metricColor(metric)]}
      axisLeft={{tickSize:0,tickPadding:9,format:formatValue}}
      axisBottom={{
        tickSize:0,
        tickPadding:12,
        tickValues:visibleDates,
        format:formatChartDate
      }}
      enableLabel={false}
      enableGridY
      animate
      tooltip={({indexValue,value}) => <ChartPopupTooltip>
        <strong>{indexValue}</strong><span>{formatValue(value)}</span>
      </ChartPopupTooltip>}
  />
}

/** 지변별 모달 컴포넌트 **/
export function ReportsInventoryModalComponent (props) {
  const [searchCondition, setSearchCondition] = useState(reportsInventoryDetail)
  const [searchState, setSearchState] = useState(reportsInventoryDetail)
  const tokenInfoState = useAtomValue(tokenResultAtom)
  const [chartData, setChartData] = useState([])
  const [chartMetric, setChartMetric] = useState("revenueAmount")
  const [isConditionOpen, setIsConditionOpen] = useState(false)
  const [downloadTrigger, setDownloadTrigger] = useState(0)

  const requestDailyData = useCallback(async condition => {
    if (tokenInfoState.role !== 'NORMAL') {
      return selectAdminStaticsInventoryDetail(
          props.userId || '',
          props.inventoryId,
          condition
      );
    }
    return selectUserStaticsInventoryDetail(
        tokenInfoState.id,
        props.inventoryId,
        condition
    );
  },[props.inventoryId,props.userId,tokenInfoState.id,tokenInfoState.role])

  const dataSource = useCallback(async ({skip, limit, sortInfo}) => {
    const condition = {
      ...searchCondition,
      pageSize: 30,
      currentPage: skip / limit === 0 ? 1 : skip / limit + 1,
      sortType: sort('DATE_DESC', sortInfo),
    };

    const response = await requestDailyData(condition);

    if (response?.content) {
      const data = response.content;

      return {
        data,
        count: response.totalElements,
      };
    }

    return {
      data: [],
      count: 0,
    };
  }, [
    requestDailyData,
    searchCondition,
  ]);

  useEffect(() => {
    requestDailyData({
      ...searchCondition,
      pageSize: 90,
      currentPage: 1,
      sortType: "DATE_ASC",
    }).then(response => {
      setChartData((response?.content || []).map(item => ({
        ...item,
        ecpm: calculateEcpm(item.revenueAmount, item.exposureCount),
      })));
    });
  },[requestDailyData,searchCondition]);

  const chartSummary = useMemo(() => chartData.reduce((sum,item) => ({
    requestCount:sum.requestCount+(Number(item.requestCount)||0),
    exposureCount:sum.exposureCount+(Number(item.exposureCount)||0),
    revenueAmount:sum.revenueAmount+(Number(item.revenueAmount)||0),
  }),{requestCount:0,exposureCount:0,revenueAmount:0}),[chartData]);

  const onSearch = () => {
    setSearchCondition({
      ...searchState
    })
  }

  return (
    <div>
      <ModalHeader variant="search" title={`${props.inventoryName !== undefined ? props.inventoryName : '지면명'} 일자별 통계`}/>
      <ModalBody>
        <DailyModalLayout>
          <PopupCard>
            <PopupSectionHeader $clickable role="button" tabIndex={0} aria-expanded={isConditionOpen}
              onClick={()=>setIsConditionOpen(open=>!open)}
              onKeyDown={e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();setIsConditionOpen(open=>!open)}}}>
              <PopupHeading><i/><div><strong>조회 조건</strong><span>광고 상품, 디바이스와 기간을 설정해 일자별 데이터를 조회합니다.</span></div></PopupHeading>
              <PopupConditionSummary>
                {!isConditionOpen && <span>{searchCondition.searchStartDate} ~ {searchCondition.searchEndDate}</span>}
                <PopupCollapseIcon $open={isConditionOpen}/>
              </PopupConditionSummary>
            </PopupSectionHeader>
            {isConditionOpen && <PopupCondition onClick={e=>e.stopPropagation()}>
              <ReportsCondition modern searchState={searchState} setSearchState={setSearchState} onSearch={onSearch} modalStyle={true}/>
            </PopupCondition>}
          </PopupCard>
          <PopupCard>
            <ChartPanel>
              <ChartPanelTop>
                <div><PopupTitle>일자별 지표 추이</PopupTitle><PopupDescription>확인할 지표를 선택하면 차트에 바로 반영됩니다.</PopupDescription></div>
                <PopupKpis>
                  <PopupKpi><small>총 요청수</small><strong>{decimalFormat(chartSummary.requestCount)}</strong><em>건</em></PopupKpi>
                  <PopupKpi><small>총 노출수</small><strong>{decimalFormat(chartSummary.exposureCount)}</strong><em>건</em></PopupKpi>
                  <PopupKpi><small>총 수익금</small><strong>{formatRevenue(chartSummary.revenueAmount)}</strong><em>원</em></PopupKpi>
                  {tokenInfoState.role !== "NORMAL" && <PopupKpi><small>평균 eCPM</small><strong>{formatEcpm(calculateEcpm(chartSummary.revenueAmount,chartSummary.exposureCount))}</strong><em>원</em></PopupKpi>}
                </PopupKpis>
              </ChartPanelTop>
              <PopupMetrics>
                {[["requestCount","요청수"],["exposureCount","노출수"],["revenueAmount","수익금"]].map(([key,label]) =>
                    <PopupMetric type="button" key={key} $active={chartMetric===key} onClick={()=>setChartMetric(key)}><i/>{label}</PopupMetric>)}
                {tokenInfoState.role !== "NORMAL" && <PopupMetric type="button" $active={chartMetric==="ecpm"} onClick={()=>setChartMetric("ecpm")}><i/>eCPM</PopupMetric>}
              </PopupMetrics>
              <PopupChart><InventoryDailyChart data={chartData} metric={chartMetric}/></PopupChart>
            </ChartPanel>
          </PopupCard>
          <PopupCard>
            <PopupSectionHeader>
              <PopupHeading><i/><div><strong>일자별 데이터</strong><span>조회된 일자별 상세 성과를 확인합니다.</span></div></PopupHeading>
              <PopupHeaderActions>
                <PopupExcelButton type="button" onClick={()=>setDownloadTrigger(value=>value+1)}>엑셀 저장</PopupExcelButton>
              </PopupHeaderActions>
            </PopupSectionHeader>
            <PopupTable>
              <HorizontalScrollGuide/>
              <Table columns={reportsStaticsInventoryDetailColumn}
                 lockedRows={lockedRows}
                 summaryReducer={summaryReducer}
                 data={dataSource}
                 pagination={true}
                 defaultSortInfo={{name:"historyDate", dir: -1}}
                 livePagination={true}
                 downloadList={false}
                 downloadTrigger={downloadTrigger}
                 fileName={`${props.inventoryName || "지면"}_일자별_통계`}
                 headerHeight={44}
                 style={{minHeight:340}}
                 scrollThreshold={0.7}/>
            </PopupTable>
          </PopupCard>
        </DailyModalLayout>
      </ModalBody>
    </div>
  )
}

/** 매체별 모달 컴포넌트**/
export function ReportsMediaModalComponent(props) {
  const [searchCondition, setSearchCondition] = useState(reportsMediaDetailAtom)
  const [searchState, setSearchState] = useState(reportsMediaDetailAtom)
  const [chartData, setChartData] = useState([])
  const [chartMetric, setChartMetric] = useState("revenueAmount")
  const [isConditionOpen, setIsConditionOpen] = useState(false)
  const [downloadTrigger, setDownloadTrigger] = useState(0)
  const requestMediaDailyData = useCallback(condition =>
      selectStaticsMediaDetail(props.userId, condition), [props.userId])

  /**
   * 데이터 페칭 (함수없이 짧게 만듬)
   * @param event
   */
  const dataSource = useCallback(async ({skip, sortInfo, limit}) => {
    const condition = {
      ...searchCondition,
      pageSize: 30,
      currentPage: skip/limit === 0 ? 1 : (skip/limit) + 1,
      sortType: sort('DATE_DESC',sortInfo)
    }
    const response = await requestMediaDailyData(condition)
    if(!response?.content) return {data:[],count:0}
    return {data:response.content,count:response.totalElements}
  }, [requestMediaDailyData,searchCondition]);

  useEffect(() => {
    requestMediaDailyData({
      ...searchCondition,pageSize:90,currentPage:1,sortType:"DATE_ASC"
    }).then(response => setChartData((response?.content || []).map(item => ({
      ...item,ecpm:calculateEcpm(item.revenueAmount,item.exposureCount),
    }))))
  },[requestMediaDailyData,searchCondition])

  const chartSummary = useMemo(() => chartData.reduce((sum,item) => ({
    requestCount:sum.requestCount+(Number(item.requestCount)||0),
    exposureCount:sum.exposureCount+(Number(item.exposureCount)||0),
    revenueAmount:sum.revenueAmount+(Number(item.revenueAmount)||0),
  }),{requestCount:0,exposureCount:0,revenueAmount:0}),[chartData])

  const onSearch = () => {
    setSearchCondition({
      ...searchState
    })
  }

  return (
    <div>
      <ModalHeader variant="search" title={`${props.siteName !== undefined ? props.siteName : '매체명'} 일자별 통계`}/>
      <ModalBody>
        <DailyModalLayout>
          <PopupCard>
            <PopupSectionHeader $clickable role="button" tabIndex={0} aria-expanded={isConditionOpen}
              onClick={()=>setIsConditionOpen(open=>!open)}
              onKeyDown={e=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();setIsConditionOpen(open=>!open)}}}>
              <PopupHeading><i/><div><strong>조회 조건</strong><span>광고 상품, 디바이스와 기간을 설정해 일자별 데이터를 조회합니다.</span></div></PopupHeading>
              <PopupConditionSummary>
                {!isConditionOpen && <span>{searchCondition.searchStartDate} ~ {searchCondition.searchEndDate}</span>}
                <PopupCollapseIcon $open={isConditionOpen}/>
              </PopupConditionSummary>
            </PopupSectionHeader>
            {isConditionOpen && <PopupCondition onClick={e=>e.stopPropagation()}>
              <ReportsCondition modern searchState={searchState} setSearchState={setSearchState} onSearch={onSearch} modalStyle={true}/>
            </PopupCondition>}
          </PopupCard>
          <PopupCard>
            <ChartPanel>
              <ChartPanelTop>
                <div><PopupTitle>일자별 지표 추이</PopupTitle><PopupDescription>확인할 지표를 선택하면 차트에 바로 반영됩니다.</PopupDescription></div>
                <PopupKpis>
                  <PopupKpi><small>총 요청수</small><strong>{decimalFormat(chartSummary.requestCount)}</strong><em>건</em></PopupKpi>
                  <PopupKpi><small>총 노출수</small><strong>{decimalFormat(chartSummary.exposureCount)}</strong><em>건</em></PopupKpi>
                  <PopupKpi><small>총 수익금</small><strong>{formatRevenue(chartSummary.revenueAmount)}</strong><em>원</em></PopupKpi>
                  <PopupKpi><small>평균 eCPM</small><strong>{formatEcpm(calculateEcpm(chartSummary.revenueAmount,chartSummary.exposureCount))}</strong><em>원</em></PopupKpi>
                </PopupKpis>
              </ChartPanelTop>
              <PopupMetrics>
                {[["requestCount","요청수"],["exposureCount","노출수"],["revenueAmount","수익금"],["ecpm","eCPM"]].map(([key,label]) =>
                    <PopupMetric type="button" key={key} $active={chartMetric===key} onClick={()=>setChartMetric(key)}><i/>{label}</PopupMetric>)}
              </PopupMetrics>
              <PopupChart><InventoryDailyChart data={chartData} metric={chartMetric}/></PopupChart>
            </ChartPanel>
          </PopupCard>
          <PopupCard>
            <PopupSectionHeader>
              <PopupHeading><i/><div><strong>일자별 데이터</strong><span>조회된 매체의 일자별 상세 성과를 확인합니다.</span></div></PopupHeading>
              <PopupHeaderActions>
                <PopupExcelButton type="button" onClick={()=>setDownloadTrigger(value=>value+1)}>엑셀 저장</PopupExcelButton>
              </PopupHeaderActions>
            </PopupSectionHeader>
            <PopupTable>
              <HorizontalScrollGuide/>
              <Table columns={reportsStaticsMediaDetailColumn}
                 lockedRows={lockedRows}
                 summaryReducer={summaryReducer}
                 defaultSortInfo={{name:"historyDate", dir: -1}}
                 data={dataSource}
                 pagination={true}
                 livePagination={true}
                 downloadList={false}
                 downloadTrigger={downloadTrigger}
                 fileName={`${props.siteName || "매체"}_일자별_통계`}
                 headerHeight={44}
                 style={{minHeight:340}}
                 scrollThreshold={0.7}/>
            </PopupTable>
          </PopupCard>
        </DailyModalLayout>
      </ModalBody>
    </div>
  )
}

const DailyModalLayout=styled.div`
  display:flex;width:100%;flex-direction:column;gap:14px;
`;
const PopupCard=styled.section`
  overflow:visible;border:1px solid #e1e6eb;border-radius:10px;background:var(--color-surface);
  box-shadow:0 4px 14px rgba(31,41,55,.04);
`;
const PopupSectionHeader=styled.div`
  display:flex;min-height:58px;align-items:center;justify-content:space-between;gap:12px;
  padding:11px 15px;border-bottom:1px solid #e9edf1;border-radius:10px 10px 0 0;background:#fbfcfe;
  cursor:${({$clickable})=>$clickable?"pointer":"default"};
  ${({$clickable})=>$clickable&&"&:hover{background:#f7faff;}"}
  &:focus-visible{outline:2px solid var(--color-primary-focus);outline-offset:-2px;}
`;
const PopupHeading=styled.div`
  display:flex;align-items:center;gap:9px;
  >i{width:3px;height:30px;border-radius:3px;background:var(--color-primary);}
  strong,span{display:block;}strong{color:var(--color-text-strong);font-size: var(--font-size-14);font-weight:750;}
  span{margin-top:4px;color:var(--color-text-muted);font-size: var(--font-size-11);}
`;
const PopupCondition=styled.div`
  >div{margin:0;padding:14px 16px 16px;background:var(--color-surface);}
`;
const PopupConditionSummary=styled.div`
  display:flex;align-items:center;gap:9px;
  >span{display:inline-flex;height:26px;align-items:center;padding:0 9px;border:1px solid #dbe7f5;
    border-radius:999px;background:var(--color-surface-muted);color:var(--color-primary-strong);font-size: var(--font-size-11);font-weight:700;white-space:nowrap;}
`;
const PopupCollapseIcon=styled.i`
  display:inline-flex;width:18px;height:26px;align-items:center;justify-content:center;
  transform:rotate(${({$open})=>$open?"180deg":"0deg"});transition:transform .18s ease;
  &::before{width:0;height:0;border-top:5px solid var(--color-primary);border-right:4px solid transparent;border-left:4px solid transparent;content:"";}
`;
const ChartPanel=styled.div`padding:16px 18px 18px;`;
const ChartPanelTop=styled.div`
  display:flex;align-items:flex-start;justify-content:space-between;gap:16px;
  @media(max-width:900px){flex-direction:column;}
`;
const PopupTitle=styled.strong`display:block;color:var(--color-text-strong);font-size: var(--font-size-14);font-weight:750;`;
const PopupDescription=styled.span`display:block;margin-top:4px;color:var(--color-text-muted);font-size: var(--font-size-11);`;
const PopupKpis=styled.div`
  display:grid;grid-template-columns:repeat(4,minmax(105px,1fr));gap:6px;
  @media(max-width:900px){width:100%;grid-template-columns:repeat(2,1fr);}
`;
const PopupKpi=styled.div`
  min-width:105px;padding:8px 10px;border:1px solid #dbe7f5;border-radius:7px;background:var(--color-surface-muted);
  small{display:block;margin-bottom:3px;color:#85909c;font-size: var(--font-size-10);}
  strong{color:#2c3947;font-size: var(--font-size-13);}em{margin-left:3px;color:#85909c;font-size: var(--font-size-10);font-style:normal;}
`;
const PopupMetrics=styled.div`display:flex;align-items:center;gap:7px;margin-top:12px;flex-wrap:wrap;`;
const PopupMetric=styled.button`
  display:inline-flex;height:29px;align-items:center;gap:6px;padding:0 10px;border:0;border-radius:6px;
  background:${({$active})=>$active?"var(--color-primary-soft)":"transparent"};color:${({$active})=>$active?"var(--color-primary-strong)":"#66727f"};
  font-size: var(--font-size-11);font-weight:700;cursor:pointer;
  i{width:7px;height:7px;border-radius:2px;background:${({$active})=>$active?"var(--color-primary)":"#cbd3dc"};}
`;
const PopupChart=styled.div`height:300px;margin-top:3px;overflow:hidden;`;
const PopupTable=styled.div`
  padding:8px 11px 12px;
  .InovuaReactDataGrid--theme-default-light{overflow:hidden;border:1px solid var(--color-border-strong);border-radius:8px;font-size: var(--font-size-13);}
  .InovuaReactDataGrid__header,.InovuaReactDataGrid__column-header{border-color:#cddff5 !important;background:#f2f7fd;}
  .InovuaReactDataGrid__header{border-bottom:2px solid #8bb8ea !important;}
`;
const PopupHeaderActions=styled.div`
  display:flex;align-items:center;justify-content:flex-end;gap:8px;
`;
const PopupExcelButton=styled.button`
  display:inline-flex;min-width:82px;height:30px;align-items:center;justify-content:center;padding:0 11px;
  border:1px solid #d7e5d9;border-radius:6px;background:#f7fbf7;color:#47774d;
  font-size: var(--font-size-11);font-weight:700;cursor:pointer;
  &:hover{border-color:#bcd6c0;background:#eef7ef;}
`;
const ChartPopupTooltip=styled.div`
  min-width:100px;padding:8px 10px;border:1px solid var(--color-border-strong);border-radius:7px;background:var(--color-surface);
  box-shadow:0 8px 22px rgba(31,41,55,.14);
  strong,span{display:block;font-size: var(--font-size-11);}strong{margin-bottom:4px;color:#6b7682;}span{color:var(--color-primary-strong);font-weight:750;}
`;

